import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Crown, Lock, Menu, Radar, UserRound, X } from "lucide-react";
import { useI18n } from "@/i18n";
import api from "@/api";
import { useMeta } from "@/contexts/MetaContext";
import { useAuth } from "@/contexts/AuthContext";

export default function HeaderNav() {
  const { lang, setLang, t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { me, clearMe } = useAuth();
  const meta = useMeta();
  const lbPublic = meta ? meta.leaderboard?.public !== false : true;
  const [signals, setSignals] = useState(0);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const burgerRef = useRef(null);

  useEffect(() => {
    api.get("/signals/count").then((r) => setSignals(r.data?.count || 0)).catch(() => setSignals(0));
  }, [location.pathname, api]);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        burgerRef.current?.focus();
      }
    };
    const onDown = (e) => {
      if (!panelRef.current?.contains(e.target) && !burgerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    clearMe();
    navigate("/");
  };

  // Hierarchy carried by SIZE and CONTRAST only — no icons on navigation entries, they would
  // add noise. Two exceptions: Premium keeps its crown (it is a call to action, not a tab) and
  // Dashboard becomes an icon alone on the right (account space, not navigation).
  const primaryCls = (path) =>
    `font-display text-[15px] font-bold uppercase tracking-wide transition-colors ${
      location.pathname === path ? "text-[#00FF87]" : "text-slate-100 hover:text-[#00FF87]"
    }`;
  const secondaryCls = (path) =>
    `font-mono2 text-[10px] uppercase tracking-wider transition-colors ${
      location.pathname === path ? "text-[#00FF87]" : "text-slate-500 hover:text-slate-300"
    }`;

  const signalsBadge = (suffix = "") =>
    signals > 0 ? (
      <span
        data-testid={`signals-badge${suffix}`}
        title={t("nav.signals_badge").replace("{n}", signals)}
        aria-label={t("nav.signals_badge").replace("{n}", signals)}
        className="ml-1.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-[#00FF87]/15 px-1.5 py-[1px] font-mono2 text-[10px] font-bold text-[#00FF87] ring-1 ring-[#00FF87]/40"
      >
        {signals > 99 ? "99+" : signals}
      </span>
    ) : null;

  const primary = [
    { to: "/watchlist", label: t("nav.watchlist"), testid: "nav-watchlist" },
    { to: "/cards", label: t("nav.cards"), testid: "nav-cards" },
    // Visible même déconnecté : le compteur de collection est une promesse du produit, pas
    // une page de compte. Un visiteur doit pouvoir la découvrir avant de s'inscrire.
    { to: "/my-collection", label: t("nav.my_collection"), testid: "nav-my-collection" },
  ];
  const secondary = [
    ...(lbPublic ? [{ to: "/leaderboard", label: t("nav.leaderboard"), testid: "nav-leaderboard" }] : []),
    { to: "/matches", label: t("nav.matches"), testid: "nav-matches" },
    { to: "/graduated", label: t("nav.graduated"), testid: "nav-graduated" },
  ];

  const langSwitcher = (suffix = "") => (
    <div className="flex overflow-hidden rounded-md border border-[#1F293D]" data-testid={`language-switcher${suffix}`}>
      {["en", "fr"].map((l) => (
        <button
          key={l}
          data-testid={`language-switcher-${l}${suffix}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 font-mono2 text-[11px] font-bold uppercase tracking-wider transition-colors ${
            lang === l ? "bg-[#00FF87] text-[#0B0E14]" : "text-slate-400 hover:text-slate-100"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <header data-testid="header-nav" className="sticky top-0 z-50 border-b border-[#1F293D] bg-[#0B0E14]/90 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center gap-2 px-3 py-3 sm:gap-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex min-w-0 items-center gap-2" data-testid="logo-link">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#00FF87]/40 bg-[#00FF87]/15 shadow-[0_0_14px_rgba(0,255,135,0.3)]">
            <Radar className="h-4 w-4 text-[#00FF87]" />
          </span>
          <span className="truncate font-display text-base font-bold uppercase tracking-wide text-slate-50 sm:text-xl">
            Rookie<span className="neon-text"> Radar</span>
          </span>
        </Link>

        <nav className="ml-6 hidden items-baseline gap-5 md:flex" data-testid="main-nav">
          {primary.map((l) => (
            <Link key={l.to} to={l.to} className={primaryCls(l.to)} data-testid={l.testid}>
              {l.label}
              {l.to === "/watchlist" && signalsBadge()}
            </Link>
          ))}
          <span className="h-4 w-px shrink-0 self-center bg-[#1F293D]" aria-hidden="true" />
          <div className="flex items-baseline gap-3.5" data-testid="secondary-nav">
            {secondary.map((l) => (
              <Link key={l.to} to={l.to} className={secondaryCls(l.to)} data-testid={l.testid}>
                {l.label}
              </Link>
            ))}
          </div>
        </nav>

        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <Link
            to="/pricing"
            data-testid="nav-premium"
            className={`hidden items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono2 text-[11px] font-bold uppercase tracking-wider transition-all md:inline-flex ${
              location.pathname === "/pricing"
                ? "border-[#FFD600] bg-[#FFD600]/20 text-[#FFD600]"
                : "border-[#FFD600]/50 bg-[#FFD600]/10 text-[#FFD600] hover:bg-[#FFD600]/20 hover:shadow-[0_0_16px_rgba(255,214,0,0.25)]"
            }`}
          >
            <Crown className="h-3 w-3" />
            {t("nav.premium")}
          </Link>
          <span className="hidden h-5 w-px shrink-0 bg-[#1F293D] md:block" aria-hidden="true" />
          <div className="hidden md:block">{langSwitcher()}</div>
          <Link
            to="/dashboard"
            data-testid="nav-dashboard"
            title={t("nav.dashboard")}
            aria-label={t("nav.dashboard")}
            className={`hidden items-center rounded-md border p-1.5 transition-colors md:inline-flex ${
              location.pathname === "/dashboard"
                ? "border-[#00FF87]/60 text-[#00FF87]"
                : "border-[#1F293D] text-slate-400 hover:border-[#00FF87]/50 hover:text-[#00FF87]"
            }`}
          >
            <UserRound className="h-4 w-4" />
          </Link>

          {me ? (
            <div className="hidden items-center gap-2 md:flex">
              <span className="hidden items-center gap-1.5 rounded-full border border-[#1F293D] px-2.5 py-1 font-mono2 text-[10px] uppercase tracking-wider text-slate-300 sm:flex" data-testid="user-chip">
                <UserRound className="h-3 w-3" />
                {me.name || me.email}
                {(me.premium_access) && <Crown className="h-3 w-3 text-[#FFD600]" />}
              </span>
              <button
                data-testid="header-sign-out"
                onClick={logout}
                className="font-mono2 text-[11px] uppercase tracking-wider text-slate-400 hover:text-red-400"
              >
                {t("nav.sign_out")}
              </button>
            </div>
          ) : (
            <Link to="/auth" data-testid="header-sign-in" className="btn-volt hidden !px-3 !py-1.5 md:inline-flex">
              {t("nav.sign_in")}
            </Link>
          )}
          {/* Admin shortcut shown only to signed-in admins; the real protection stays
              server-side (404 for non-admins on every /api/admin/* endpoint). */}
          {me?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="admin-nav-link"
              className="flex items-center gap-1.5 rounded-md border border-[#1F293D] px-2.5 py-1.5 font-mono2 text-[11px] uppercase tracking-wider text-slate-300 transition-colors hover:border-[#00F0FF]/50 hover:text-[#00F0FF]"
            >
              <Lock className="h-3 w-3" />
              <span className="hidden sm:inline">{t("nav.admin")}</span>
            </Link>
          )}
          <button
            ref={burgerRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-menu-panel"
            data-testid="mobile-menu-toggle"
            className="flex shrink-0 items-center gap-1.5 rounded-md border border-[#00FF87]/50 bg-[#00FF87]/10 px-2.5 py-2 font-mono2 text-[11px] font-bold uppercase tracking-wider text-[#00FF87] transition-colors hover:bg-[#00FF87]/20 md:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            {t("nav.menu")}
          </button>
        </div>
      </div>

      {open && (
        <div
          id="mobile-menu-panel"
          ref={panelRef}
          data-testid="mobile-menu-panel"
          className="border-t border-[#1F293D] bg-[#0B0E14] px-3 pb-4 pt-2 md:hidden"
        >
          <nav className="flex flex-col" data-testid="mobile-nav">
            {primary.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${l.testid}`}
                className={`${primaryCls(l.to)} border-b border-[#1F293D]/60 py-3.5`}
              >
                {l.label}
                {l.to === "/watchlist" && signalsBadge("-mobile")}
              </Link>
            ))}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 py-3" data-testid="mobile-secondary-nav">
              {secondary.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-${l.testid}`}
                  className={secondaryCls(l.to)}
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </nav>
          <Link
            to="/pricing"
            onClick={() => setOpen(false)}
            data-testid="mobile-nav-premium"
            className="flex items-center justify-center gap-1.5 rounded-full border border-[#FFD600]/50 bg-[#FFD600]/10 py-2 font-mono2 text-[11px] font-bold uppercase tracking-wider text-[#FFD600]"
          >
            <Crown className="h-3 w-3" />
            {t("nav.premium")}
          </Link>
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#1F293D]/60 pt-3">
            {langSwitcher("-mobile")}
            <div className="flex items-center gap-3">
              <Link
                to="/dashboard"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-dashboard"
                title={t("nav.dashboard")}
                aria-label={t("nav.dashboard")}
                className="rounded-md border border-[#1F293D] p-1.5 text-slate-400"
              >
                <UserRound className="h-4 w-4" />
              </Link>
              {me ? (
                <button
                  data-testid="mobile-sign-out"
                  onClick={() => { setOpen(false); logout(); }}
                  className="font-mono2 text-[11px] uppercase tracking-wider text-slate-400 hover:text-red-400"
                >
                  {t("nav.sign_out")}
                </button>
              ) : (
                <Link to="/auth" onClick={() => setOpen(false)} data-testid="mobile-sign-in" className="btn-volt !px-3 !py-1.5">
                  {t("nav.sign_in")}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
